import type { InstrumentId, Timeframe } from "@trader/shared/MarketData"
import { useSyncExternalStore } from "react"
import { documentSaver, loadDocument } from "../../documents.ts"
import { decodeIndicatorStyle, type IndicatorStyle } from "./indicatorStyleSchema.ts"
import type { LayoutId } from "./layouts.ts"
import {
  decodeWorkspace,
  DEFAULT_WORKSPACE,
  editIndicators,
  type IndicatorInstance,
  type IndicatorPatch,
  type PaneState,
  type StrategyInstance,
  withActiveShown,
  type WorkspaceState
} from "./workspaceModel.ts"

// Which charts are open, how they're arranged, their shared indicators, and each
// chart's strategy (the document's shape and rules: workspaceModel.ts). Saved on
// the server ("workspace" document) so it follows the user across browsers;
// localStorage keeps a copy so the page opens instantly.

export type { IndicatorInstance, IndicatorPatch, PaneState, StrategyInstance, WorkspaceState } from "./workspaceModel.ts"
export { paneIndicators, sharedIndicators } from "./workspaceModel.ts"

const STORAGE_KEY = "trader.workspace.v1"
const SERVER_KEY = "workspace"

const newUid = () => crypto.randomUUID()
const parse = (json: string | null): WorkspaceState | null => decodeWorkspace(json, newUid)

const loadLocal = (): WorkspaceState => {
  try {
    return parse(localStorage.getItem(STORAGE_KEY)) ?? DEFAULT_WORKSPACE
  } catch {
    // Storage blocked (private mode): use defaults.
    return DEFAULT_WORKSPACE
  }
}

let state = loadLocal()
const listeners = new Set<() => void>()
// One save in flight, retried on network errors, sent as the page hides (documentSaver.ts).
const saver = documentSaver(1_000)

const setState = (next: WorkspaceState) => {
  state = next
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Not cached locally; the server copy still is.
  }
  for (const listener of listeners) listener()
}

const update = (next: WorkspaceState) => {
  setState(next)
  saver.schedule(SERVER_KEY, () => JSON.stringify(state))
}

/** Sends a pending workspace save now (before signing out). */
export const flushWorkspace = (): Promise<void> => saver.flush()

/** Drops pending workspace saves (another user signed in). */
export const discardWorkspaceSaves = (): void => saver.discard()

/** Replaces the local workspace with the server's copy, if there is one. Called once after sign-in. */
/** Whose workspace this browser's copy is (another user signing in here must not get it). */
const OWNER_KEY = "trader.workspace.owner"

const localOwner = (): string | null => {
  try {
    return localStorage.getItem(OWNER_KEY)
  } catch {
    return null
  }
}

/**
 * Replaces the local workspace with the server's copy for this user. When the
 * server has none and this browser's copy belongs to someone else (or to nobody
 * known), the user starts from the defaults: another user's charts are never
 * shown as theirs, nor saved as theirs. Called once after sign-in.
 */
export const syncWorkspaceFromServer = async (userId: string): Promise<void> => {
  const saved = parse(await loadDocument(SERVER_KEY).catch(() => null))
  const ownedHere = localOwner() === userId
  try {
    localStorage.setItem(OWNER_KEY, userId)
  } catch {
    // Not remembered; the server copy still decides next time.
  }
  if (saved !== null) setState(saved)
  else if (!ownedHere) setState(DEFAULT_WORKSPACE)
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useWorkspace = (): WorkspaceState => useSyncExternalStore(subscribe, () => state)

/** The workspace now, outside React (e.g. the charts a replay should follow when Play is pressed). */
export const currentWorkspace = (): WorkspaceState => state

export const setLayout = (layout: LayoutId): void => update(withActiveShown({ ...state, layout }))

export const setActivePane = (active: number): void => {
  if (active !== state.active) update({ ...state, active })
}

const updatePane = (index: number, change: (pane: PaneState) => PaneState) =>
  update({ ...state, panes: state.panes.map((pane, i) => (i === index ? change(pane) : pane)) })

export const setInstrument = (instrumentId: InstrumentId): void =>
  updatePane(state.active, (pane) => ({ ...pane, instrumentId }))

export const setTimeframe = (timeframe: Timeframe): void => updatePane(state.active, (pane) => ({ ...pane, timeframe }))

// --- Indicators: one list, shown by every chart -----------------------------------

/**
 * Adds an indicator with its default inputs to every chart. `initialStyle` sees
 * the indicators already there (e.g. to give a second EMA another colour).
 */
export const addIndicator = (
  id: string,
  initialStyle?: (existing: ReadonlyArray<IndicatorInstance>) => IndicatorStyle | undefined
): void =>
  update(editIndicators(state, (list) => {
    const style = initialStyle?.(list)
    const checked = style === undefined ? null : decodeIndicatorStyle(style)
    return [...list, { uid: newUid(), id, inputs: {}, hidden: false, ...(checked ? { style: checked } : {}) }]
  }))

/** Changes an indicator on every chart. A style is checked against the schema before it's stored. */
export const updateIndicator = (uid: string, patch: IndicatorPatch): void => {
  const checked: IndicatorPatch = patch.style === undefined ? patch : { ...patch, style: decodeIndicatorStyle(patch.style) ?? undefined }
  update(editIndicators(state, (list) => list.map((indicator) => (indicator.uid === uid ? { ...indicator, ...checked } : indicator))))
}

export const removeIndicator = (uid: string): void =>
  update(editIndicators(state, (list) => list.filter((indicator) => indicator.uid !== uid)))

// --- Strategies: one per chart ----------------------------------------------------

/** Puts a strategy on the active pane (one per pane, as the Strategy Tester shows one). */
export const setStrategy = (id: string): void =>
  updatePane(state.active, (pane) => ({ ...pane, strategy: { id, inputs: {}, properties: {}, deep: false, hidden: false } }))

/**
 * Another version of the same strategy on a pane (a user strategy's draft, or
 * the version just saved from it): its inputs, properties and options stay.
 * Without a strategy there, it is put on fresh.
 */
export const replaceStrategyId = (paneIndex: number, id: string): void =>
  updatePane(paneIndex, (pane) => ({
    ...pane,
    strategy: pane.strategy ? { ...pane.strategy, id } : { id, inputs: {}, properties: {}, deep: false, hidden: false }
  }))

export const updateStrategy = (paneIndex: number, patch: Partial<Omit<StrategyInstance, "id">>): void =>
  updatePane(paneIndex, (pane) => (pane.strategy ? { ...pane, strategy: { ...pane.strategy, ...patch } } : pane))

/** Starts the pane's backtest at a bar time (epoch seconds), or at the first bar again (null). */
export const setStrategyStart = (paneIndex: number, from: number | null): void =>
  updatePane(paneIndex, (pane) => {
    if (!pane.strategy) return pane
    const { from: _previous, ...rest } = pane.strategy
    return { ...pane, strategy: from === null ? rest : { ...rest, from } }
  })

/**
 * The pane's Backtesting range (Properties): bars opening at `from` or later and before `to`
 * (epoch seconds); null leaves that side open.
 */
export const setStrategyRange = (paneIndex: number, from: number | null, to: number | null): void =>
  updatePane(paneIndex, (pane) => {
    if (!pane.strategy) return pane
    const { from: _from, to: _to, ...rest } = pane.strategy
    return { ...pane, strategy: { ...rest, ...(from === null ? {} : { from }), ...(to === null ? {} : { to }) } }
  })

export const removeStrategy = (paneIndex: number): void => updatePane(paneIndex, (pane) => ({ ...pane, strategy: null }))
